import type { BellSchedule, Period } from '@/types'

/**
 * 行语义分类（用于详情页调课网格过滤：仅展示 `teaching` + `rest-lunch`）。
 */
export type BellScheduleTimelineKind =
  | 'teaching'
  | 'rest-short'
  | 'rest-long'
  | 'rest-lunch'
  | 'rest-bridge'

/**
 * 与作息方案 `periods` JSON 纵向行序列一致（显式时段 + morningStudyToClassBreak / afternoonToEveningBreak）。
 * enginePeriod：与排课引擎 / 草稿 occurrence 使用的节次序号一致（仅早自习、正课、晚自习等可排课行有值）。
 */
export type BellScheduleDisplayRow = {
  key: string
  rowId: string
  name: string
  enginePeriod: number | null
  timelineKind: BellScheduleTimelineKind
}

let engineSeq = 0

function resetEngine(): void {
  engineSeq = 0
}

function restTimelineKind(period: Period, keySuffix: string): BellScheduleTimelineKind {
  if (period.type === 'lunch' || (period.type === 'break' && period.name.includes('午休'))) {
    return 'rest-lunch'
  }
  if (period.type === 'morning-exercise') {
    return 'rest-long'
  }
  if (keySuffix === 'mscb' || keySuffix === 'ateb') {
    return 'rest-bridge'
  }
  return 'rest-short'
}

function pushSchedulable(rows: BellScheduleDisplayRow[], period: Period, rowId: string): void {
  engineSeq += 1
  rows.push({
    key: period.id || `slot-${engineSeq}`,
    rowId,
    name: period.name,
    enginePeriod: engineSeq,
    timelineKind: 'teaching'
  })
}

function pushNonSchedulable(
  rows: BellScheduleDisplayRow[],
  period: Period,
  keySuffix: string,
  rowId = keySuffix
): void {
  rows.push({
    key: period.id || keySuffix,
    rowId,
    name: period.name,
    enginePeriod: null,
    timelineKind: restTimelineKind(period, keySuffix)
  })
}

/**
 * 将作息方案展开为与 `buildDefaultBellSchedulePeriods` 一致的行顺序。
 */
export function buildBellScheduleDisplayTimeline(
  bell: BellSchedule | null | undefined
): BellScheduleDisplayRow[] {
  if (!bell?.periods) {
    return []
  }

  const p = bell.periods
  const rows: BellScheduleDisplayRow[] = []
  resetEngine()

  let sbIdx = 0

  for (let i = 0; i < p.morningStudy.length; i += 1) {
    pushSchedulable(rows, p.morningStudy[i], `mr-${i}`)
    if (i < p.morningStudy.length - 1) {
      const br = p.morningShortBreaks[sbIdx]
      if (br) {
        pushNonSchedulable(rows, br, `msb-${sbIdx}`)
        sbIdx += 1
      }
    }
  }

  if (p.morningStudyToClassBreak != null) {
    pushNonSchedulable(rows, p.morningStudyToClassBreak, 'mscb')
  }

  const morningBreakPos = p.morningBreakPosition ?? 0

  for (let i = 0; i < p.morning.length; i += 1) {
    pushSchedulable(rows, p.morning[i], `c-${i}`)
    if (i >= p.morning.length - 1) {
      break
    }
    if (p.morningBreak != null && i === morningBreakPos) {
      pushNonSchedulable(rows, p.morningBreak, 'mbb')
    } else {
      const br = p.morningShortBreaks[sbIdx]
      if (br) {
        pushNonSchedulable(rows, br, `msb-${sbIdx}`)
        sbIdx += 1
      }
    }
  }

  if (p.lunchBreak != null) {
    pushNonSchedulable(rows, p.lunchBreak, 'lunch')
  }

  let asbIdx = 0
  const afternoonBreakPos = p.afternoonBreakPosition ?? 0

  for (let i = 0; i < p.afternoon.length; i += 1) {
    pushSchedulable(rows, p.afternoon[i], `ac-${i}`)
    if (i >= p.afternoon.length - 1) {
      break
    }
    if (p.afternoonBreak != null && i === afternoonBreakPos) {
      pushNonSchedulable(rows, p.afternoonBreak, 'abb')
    } else {
      const br = p.afternoonShortBreaks[asbIdx]
      if (br) {
        pushNonSchedulable(rows, br, `asb-${asbIdx}`)
        asbIdx += 1
      }
    }
  }

  if (p.afternoonToEveningBreak != null) {
    pushNonSchedulable(rows, p.afternoonToEveningBreak, 'ateb')
  }

  let esbIdx = 0
  for (let i = 0; i < p.eveningStudy.length; i += 1) {
    pushSchedulable(rows, p.eveningStudy[i], `es-${i}`)
    if (i < p.eveningStudy.length - 1) {
      const br = p.eveningStudyShortBreaks[esbIdx]
      if (br) {
        pushNonSchedulable(rows, br, `esb-${esbIdx}`)
        esbIdx += 1
      }
    }
  }

  return rows
}

/**
 * 返回每个真实大课间之后的首个引擎节次号。大课间按作息结构节点识别，
 * 不使用相邻课程时长差推断。
 */
export function resolveLongBreakStartEnginePeriods(
  bell: BellSchedule | null | undefined
): number[] {
  const result: number[] = []
  let pendingLongBreak = false

  for (const row of buildBellScheduleDisplayTimeline(bell)) {
    if (row.timelineKind === 'rest-long' || row.rowId === 'mbb' || row.rowId === 'abb') {
      pendingLongBreak = true
      continue
    }
    if (row.timelineKind === 'teaching') {
      if (pendingLongBreak && row.enginePeriod != null) {
        result.push(row.enginePeriod)
      }
      pendingLongBreak = false
    }
  }

  return result
}

/**
 * 排课详情页「调课」网格：只保留「上课」行与午休横幅，不展示普通课间 / 大课间 / 桥接课间等。
 */
export function filterScheduleDetailTimelineRows(rows: BellScheduleDisplayRow[]): BellScheduleDisplayRow[] {
  return rows.filter((r) => r.timelineKind === 'teaching' || r.timelineKind === 'rest-lunch')
}

export function countSchedulableRows(rows: BellScheduleDisplayRow[]): number {
  return rows.filter((r) => r.enginePeriod != null).length
}

export function isBellScheduleDisplayCellActive(
  bellSchedule: BellSchedule | null | undefined,
  row: BellScheduleDisplayRow,
  dayOfWeek: number
): boolean {
  if (!bellSchedule || row.enginePeriod == null) {
    return false
  }

  if (!bellSchedule.config.weekdays.includes(dayOfWeek)) {
    return false
  }

  const rawActiveCellKeys = bellSchedule.config.activeCellKeys
  if (rawActiveCellKeys == null) {
    return true
  }

  const activeCellKeys = new Set(rawActiveCellKeys)
  return activeCellKeys.has(`${row.rowId}-${dayOfWeek}`)
}
